import React from 'react';
import {
  Box,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
  Button,
  Checkbox,
  Alert,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EditIcon from '@mui/icons-material/Edit';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { Tooltip } from '@mui/material';
import { downloadRecordsCsv } from '../utils/csvExport';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  UserRow,
  createUser,
  listUsers,
  toggleUserStatus,
  updateUser,
  type ImportedUserRow,
} from '../api/users';
import { deleteDocsInCollection } from '../api/batchDelete';
import { useAuth } from '../auth/AuthContext';

const formatDate = (date?: Date) =>
  date ? date.toLocaleDateString() : '—';

const statusColor = (status?: string) => {
  switch (status) {
    case 'active':
      return 'success';
    case 'inactive':
      return 'default';
    default:
      return 'default';
  }
};

export const UsersPage: React.FC = () => {
  const queryClient = useQueryClient();
  const { role } = useAuth();
  const canEdit = role === 'admin';
  const { data, isLoading } = useQuery<UserRow[]>({
    queryKey: ['users'],
    queryFn: listUsers,
  });

  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [search, setSearch] = React.useState('');
  const [roleFilter, setRoleFilter] = React.useState<string>('all');
  const [statusFilter, setStatusFilter] = React.useState<string>('all');
  const [orderBy, setOrderBy] = React.useState<
    'name' | 'email' | 'phone' | 'role' | 'status' | 'createdAt' | 'updatedAt'
  >('name');
  const [order, setOrder] = React.useState<'asc' | 'desc'>('asc');
  const [editUser, setEditUser] = React.useState<UserRow | null>(null);
  const [editFirstName, setEditFirstName] = React.useState('');
  const [editLastName, setEditLastName] = React.useState('');
  const [editAuthUid, setEditAuthUid] = React.useState('');
  const [editEmail, setEditEmail] = React.useState('');
  const [editPhone, setEditPhone] = React.useState('');
  const [editAcademy, setEditAcademy] = React.useState('');
  const [editCap, setEditCap] = React.useState('');
  const [editRegion, setEditRegion] = React.useState('');
  const [editCommune, setEditCommune] = React.useState('');
  const [editSpeciality, setEditSpeciality] = React.useState('');
  const [editRole, setEditRole] = React.useState<string>('teacher');
  const [editStatus, setEditStatus] = React.useState<'active' | 'inactive'>('active');
  const [selectedIds, setSelectedIds] = React.useState<string[]>([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [deleteError, setDeleteError] = React.useState<string | null>(null);
  const [deleting, setDeleting] = React.useState(false);

  const [createOpen, setCreateOpen] = React.useState(false);
  const [createEmail, setCreateEmail] = React.useState('');
  const [createAuthUid, setCreateAuthUid] = React.useState('');
  const [createFirstName, setCreateFirstName] = React.useState('');
  const [createLastName, setCreateLastName] = React.useState('');
  const [createPhone, setCreatePhone] = React.useState('');
  const [createAcademy, setCreateAcademy] = React.useState('');
  const [createCap, setCreateCap] = React.useState('');
  const [createRegion, setCreateRegion] = React.useState('');
  const [createCommune, setCreateCommune] = React.useState('');
  const [createSpeciality, setCreateSpeciality] = React.useState('');
  const [createRole, setCreateRole] = React.useState<string>('teacher');
  const [createStatus, setCreateStatus] = React.useState<'active' | 'inactive'>('active');
  const [createSaving, setCreateSaving] = React.useState(false);
  const [createError, setCreateError] = React.useState<string | null>(null);

  const handleRequestSort = (
    property:
      | 'name'
      | 'email'
      | 'phone'
      | 'role'
      | 'status'
      | 'createdAt'
      | 'updatedAt'
  ) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const filtered = React.useMemo(() => {
    if (!data) return [];
    return data.filter((u) => {
      const haystack = `${u.firstName ?? ''} ${u.lastName ?? ''} ${
        u.email ?? ''
      } ${u.phone ?? ''} ${u.academy ?? ''} ${u.cap ?? ''} ${u.region ?? ''} ${
        u.commune ?? ''
      } ${u.speciality ?? ''}`.toLowerCase();
      const matchesSearch = haystack.includes(search.toLowerCase());
      const matchesRole =
        roleFilter === 'all' || (u.role ?? 'UNKNOWN') === roleFilter;
      const matchesStatus =
        statusFilter === 'all' || (u.status ?? 'active') === statusFilter;
      return matchesSearch && matchesRole && matchesStatus;
    });
  }, [data, search, roleFilter, statusFilter]);

  const sorted = React.useMemo(() => {
    const arr = [...filtered];
    arr.sort((a, b) => {
      const factor = order === 'asc' ? 1 : -1;
      const getName = (u: UserRow) =>
        `${u.lastName ?? ''} ${u.firstName ?? ''}`.trim().toLowerCase();
      let av: string | number | Date | undefined;
      let bv: string | number | Date | undefined;

      switch (orderBy) {
        case 'name':
          av = getName(a);
          bv = getName(b);
          break;
        case 'email':
          av = (a.email ?? '').toLowerCase();
          bv = (b.email ?? '').toLowerCase();
          break;
        case 'phone':
          av = (a.phone ?? '').toLowerCase();
          bv = (b.phone ?? '').toLowerCase();
          break;
        case 'role':
          av = (a.role ?? '').toString().toLowerCase();
          bv = (b.role ?? '').toString().toLowerCase();
          break;
        case 'status':
          av = (a.status ?? '').toString().toLowerCase();
          bv = (b.status ?? '').toString().toLowerCase();
          break;
        case 'createdAt':
          av = a.createdAt ?? new Date(0);
          bv = b.createdAt ?? new Date(0);
          break;
        case 'updatedAt':
          av = a.updatedAt ?? new Date(0);
          bv = b.updatedAt ?? new Date(0);
          break;
        default:
          av = 0;
          bv = 0;
      }

      if (av instanceof Date && bv instanceof Date) {
        return (av.getTime() - bv.getTime()) * factor;
      }

      const as = String(av);
      const bs = String(bv);
      if (as < bs) return -1 * factor;
      if (as > bs) return 1 * factor;
      return 0;
    });
    return arr;
  }, [filtered, order, orderBy]);

  const paginated = React.useMemo(() => {
    const start = page * rowsPerPage;
    return sorted.slice(start, start + rowsPerPage);
  }, [sorted, page, rowsPerPage]);

  const allSelected =
    canEdit &&
    filtered.length > 0 &&
    filtered.every((u) => selectedIds.includes(u.id));
  const someSelected =
    canEdit &&
    filtered.length > 0 &&
    filtered.some((u) => selectedIds.includes(u.id)) &&
    !allSelected;

  const toggleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedIds(filtered.map((u) => u.id));
    } else {
      setSelectedIds([]);
    }
  };

  const toggleOne = (id: string) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleConfirmDeleteSelected = async () => {
    if (!selectedIds.length) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await deleteDocsInCollection('users', selectedIds);
      await queryClient.invalidateQueries({ queryKey: ['users'] });
      setSelectedIds([]);
      setDeleteDialogOpen(false);
    } catch (e) {
      setDeleteError(e instanceof Error ? e.message : "La suppression a échoué.");
    } finally {
      setDeleting(false);
    }
  };

  const handleToggleStatus = async (user: UserRow) => {
    await toggleUserStatus(user.id, user.status);
    await queryClient.invalidateQueries({ queryKey: ['users'] });
  };

  const openEditDialog = (user: UserRow) => {
    setEditUser(user);
    setEditAuthUid(user.authUid ?? '');
    setEditFirstName(user.firstName ?? '');
    setEditLastName(user.lastName ?? '');
    setEditEmail(user.email ?? '');
    setEditPhone(user.phone ?? '');
    setEditAcademy(user.academy ?? '');
    setEditCap(user.cap ?? '');
    setEditRegion(user.region ?? '');
    setEditCommune(user.commune ?? '');
    setEditSpeciality(user.speciality ?? '');
    setEditRole(user.role && user.role !== 'UNKNOWN' ? user.role : 'teacher');
    setEditStatus(user.status ?? 'active');
  };

  const closeEditDialog = () => {
    setEditUser(null);
  };

  const openCreateUser = () => {
    setEditUser(null);
    setCreateEmail('');
    setCreateAuthUid('');
    setCreateFirstName('');
    setCreateLastName('');
    setCreatePhone('');
    setCreateAcademy('');
    setCreateCap('');
    setCreateRegion('');
    setCreateCommune('');
    setCreateSpeciality('');
    setCreateRole('teacher');
    setCreateStatus('active');
    setCreateError(null);
    setCreateOpen(true);
  };

  const closeCreateUser = () => {
    if (createSaving) return;
    setCreateOpen(false);
    setCreateError(null);
  };

  const handleSaveCreateUser = async () => {
    if (!createFirstName.trim() || !createLastName.trim()) {
      setCreateError('Le prénom et le nom sont obligatoires.');
      return;
    }
    const row: ImportedUserRow = {
      authUid: createAuthUid.trim() || undefined,
      firstName: createFirstName.trim(),
      lastName: createLastName.trim(),
      email: createEmail.trim() || undefined,
      phone: createPhone.trim() || undefined,
      academy: createAcademy.trim() || undefined,
      cap: createCap.trim() || undefined,
      region: createRegion.trim() || undefined,
      commune: createCommune.trim() || undefined,
      speciality: createSpeciality.trim() || undefined,
      role: createRole as ImportedUserRow['role'],
      status: createStatus,
    };
    setCreateSaving(true);
    setCreateError(null);
    try {
      await createUser(row);
      await queryClient.invalidateQueries({ queryKey: ['users'] });
      setCreateOpen(false);
      setPage(0);
    } catch (e) {
      setCreateError(e instanceof Error ? e.message : 'Création impossible.');
    } finally {
      setCreateSaving(false);
    }
  };

  const handleSaveUser = async () => {
    if (!editUser) return;
    await updateUser(editUser.id, {
      authUid: editAuthUid.trim() || undefined,
      firstName: editFirstName.trim(),
      lastName: editLastName.trim(),
      email: editEmail.trim() || null,
      phone: editPhone.trim() || undefined,
      academy: editAcademy.trim() || undefined,
      cap: editCap.trim() || undefined,
      region: editRegion.trim() || undefined,
      commune: editCommune.trim() || undefined,
      speciality: editSpeciality.trim() || undefined,
      role: editRole as any,
      status: editStatus,
    });
    await queryClient.invalidateQueries({ queryKey: ['users'] });
    closeEditDialog();
  };

  const handleExportCsv = () => {
    const rows: Array<Record<string, unknown>> = sorted.map((u) => ({
      userId: u.id,
      authUid: u.authUid ?? '',
      firstName: u.firstName ?? '',
      lastName: u.lastName ?? '',
      email: u.email ?? '',
      phone: u.phone ?? '',
      academy: u.academy ?? '',
      cap: u.cap ?? '',
      region: u.region ?? '',
      commune: u.commune ?? '',
      speciality: u.speciality ?? '',
      role: u.role ?? '',
      status: u.status ?? '',
      createdAt: u.createdAt,
      updatedAt: u.updatedAt,
    }));
    downloadRecordsCsv('utilisateurs', rows);
  };

  return (
    <>
    <Paper sx={{ p: 2 }}>
      <Typography variant="h5" gutterBottom>
        Utilisateurs
      </Typography>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 2,
          mb: 2,
          alignItems: 'center',
        }}
      >
        <TextField
          size="small"
          label="Rechercher"
          placeholder="Nom, email, téléphone..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(0);
          }}
        />
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel id="role-filter-label">Rôle</InputLabel>
          <Select
            labelId="role-filter-label"
            label="Rôle"
            value={roleFilter}
            onChange={(e) => {
              setRoleFilter(e.target.value);
              setPage(0);
            }}
          >
            <MenuItem value="all">Tous</MenuItem>
            <MenuItem value="admin">Admin</MenuItem>
            <MenuItem value="cap">CAP</MenuItem>
            <MenuItem value="responsible">Responsable</MenuItem>
            <MenuItem value="teacher">Enseignant</MenuItem>
            <MenuItem value="cgs">CGS</MenuItem>
            <MenuItem value="drh">DRH</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel id="status-filter-label">Statut</InputLabel>
          <Select
            labelId="status-filter-label"
            label="Statut"
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(0);
            }}
          >
            <MenuItem value="all">Tous</MenuItem>
            <MenuItem value="active">Actif</MenuItem>
            <MenuItem value="inactive">Inactif</MenuItem>
          </Select>
        </FormControl>
        <Box sx={{ display: 'flex', gap: 1, ml: { xs: 0, sm: 'auto' }, flexWrap: 'wrap' }}>
          {canEdit && (
            <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={openCreateUser}>
              Ajouter
            </Button>
          )}
          <Button
            variant="outlined"
            size="small"
            startIcon={<FileDownloadIcon />}
            disabled={!sorted.length}
            onClick={handleExportCsv}
          >
            Exporter CSV
          </Button>
        </Box>
      </Box>
      {canEdit && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 1,
            gap: 2,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            {selectedIds.length
              ? `${selectedIds.length} sélectionné(s)`
              : 'Aucune sélection'}
          </Typography>
          <Button
            size="small"
            variant="outlined"
            color="error"
            disabled={!selectedIds.length}
            onClick={() => {
              setDeleteError(null);
              setDeleteDialogOpen(true);
            }}
          >
            Supprimer la sélection
          </Button>
        </Box>
      )}
      {deleteError && (
        <Alert severity="error" sx={{ mb: 1 }} onClose={() => setDeleteError(null)}>
          {deleteError}
        </Alert>
      )}
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              {canEdit && (
                <TableCell padding="checkbox">
                  <Checkbox
                    size="small"
                    indeterminate={someSelected}
                    checked={allSelected}
                    onChange={(e) => toggleSelectAll(e.target.checked)}
                  />
                </TableCell>
              )}
              <TableCell sortDirection={orderBy === 'name' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'name'}
                  direction={orderBy === 'name' ? order : 'asc'}
                  onClick={() => handleRequestSort('name')}
                >
                  Nom
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={orderBy === 'email' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'email'}
                  direction={orderBy === 'email' ? order : 'asc'}
                  onClick={() => handleRequestSort('email')}
                >
                  Email
                </TableSortLabel>
              </TableCell>
              {/* <TableCell>Auth UID</TableCell> */}
              <TableCell sortDirection={orderBy === 'phone' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'phone'}
                  direction={orderBy === 'phone' ? order : 'asc'}
                  onClick={() => handleRequestSort('phone')}
                >
                  Téléphone
                </TableSortLabel>
              </TableCell>
              <TableCell>Academy</TableCell>
              <TableCell>CAP</TableCell>
              <TableCell>Région</TableCell>
              <TableCell>Commune</TableCell>
              <TableCell>Spécialité</TableCell>
              <TableCell sortDirection={orderBy === 'role' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'role'}
                  direction={orderBy === 'role' ? order : 'asc'}
                  onClick={() => handleRequestSort('role')}
                >
                  Rôle
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={orderBy === 'status' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'status'}
                  direction={orderBy === 'status' ? order : 'asc'}
                  onClick={() => handleRequestSort('status')}
                >
                  Statut
                </TableSortLabel>
              </TableCell>
              <TableCell
                sortDirection={orderBy === 'createdAt' ? order : false}
              >
                <TableSortLabel
                  active={orderBy === 'createdAt'}
                  direction={orderBy === 'createdAt' ? order : 'asc'}
                  onClick={() => handleRequestSort('createdAt')}
                >
                  Créé le
                </TableSortLabel>
              </TableCell>
              <TableCell
                sortDirection={orderBy === 'updatedAt' ? order : false}
              >
                <TableSortLabel
                  active={orderBy === 'updatedAt'}
                  direction={orderBy === 'updatedAt' ? order : 'asc'}
                  onClick={() => handleRequestSort('updatedAt')}
                >
                  Mis à jour le
                </TableSortLabel>
              </TableCell>
              {canEdit && <TableCell align="right">Actions</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={canEdit ? 15 : 13}>Chargement...</TableCell>
              </TableRow>
            )}
            {!isLoading &&
              paginated.map((u) => (
                <TableRow key={u.id} hover selected={canEdit && selectedIds.includes(u.id)}>
                  {canEdit && (
                    <TableCell padding="checkbox">
                      <Checkbox
                        size="small"
                        checked={selectedIds.includes(u.id)}
                        onChange={() => toggleOne(u.id)}
                      />
                    </TableCell>
                  )}
                  <TableCell>
                    {u.lastName ?? ''} {u.firstName ?? ''}
                  </TableCell>
                  <TableCell>{u.email ?? '—'}</TableCell>
                  {/* <TableCell>{u.authUid ?? '—'}</TableCell> */}
                  <TableCell>{u.phone ?? '—'}</TableCell>
                  <TableCell>{u.academy ?? '—'}</TableCell>
                  <TableCell>{u.cap ?? '—'}</TableCell>
                  <TableCell>{u.region ?? '—'}</TableCell>
                  <TableCell>{u.commune ?? '—'}</TableCell>
                  <TableCell>{u.speciality ?? '—'}</TableCell>
                  <TableCell>{u.role ?? 'UNKNOWN'}</TableCell>
                  <TableCell>
                    <Chip
                      label={u.status ?? 'active'}
                      color={statusColor(u.status) as any}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{formatDate(u.createdAt)}</TableCell>
                  <TableCell>{formatDate(u.updatedAt)}</TableCell>
                  {canEdit && (
                    <TableCell align="right" sx={{ verticalAlign: 'middle' }}>
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'row',
                          alignItems: 'center',
                          justifyContent: 'flex-end',
                          flexWrap: 'nowrap',
                          gap: 0.25,
                        }}
                      >
                        <Tooltip title="Modifier l'utilisateur">
                          <IconButton size="small" onClick={() => openEditDialog(u)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip
                          title={
                            u.status === 'active'
                              ? 'Désactiver cet utilisateur'
                              : 'Activer cet utilisateur'
                          }
                        >
                          <IconButton
                            size="small"
                            onClick={() => handleToggleStatus(u)}
                          >
                            {u.status === 'active' ? (
                              <BlockIcon fontSize="small" />
                            ) : (
                              <CheckCircleIcon fontSize="small" />
                            )}
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            {!isLoading && !filtered.length && (
              <TableRow>
                <TableCell colSpan={canEdit ? 15 : 13}>
                  <Typography variant="body2" color="text.secondary">
                    Aucun utilisateur trouvé.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={filtered.length}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[10, 25, 50, 100, 500]}
      />
      <Dialog open={deleteDialogOpen} onClose={() => !deleting && setDeleteDialogOpen(false)}>
        <DialogTitle>Supprimer les utilisateurs sélectionnés</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {selectedIds.length} profil(s) Firestore seront supprimés. Les comptes Firebase Auth ne
            sont pas supprimés automatiquement.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>
            Annuler
          </Button>
          <Button onClick={handleConfirmDeleteSelected} color="error" variant="contained" disabled={deleting}>
            {deleting ? 'Suppression…' : 'Supprimer'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
      <Dialog open={createOpen} onClose={closeCreateUser} maxWidth="sm" fullWidth>
        <DialogTitle>Nouvel utilisateur</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          {createError && (
            <Alert severity="error" onClose={() => setCreateError(null)}>
              {createError}
            </Alert>
          )}
          <TextField
            label="Auth UID"
            value={createAuthUid}
            onChange={(e) => setCreateAuthUid(e.target.value)}
            size="small"
            disabled={createSaving}
            helperText="Optionnel — UID Firebase Auth à lier au profil"
          />
          <TextField
            label="Prénom"
            required
            value={createFirstName}
            onChange={(e) => setCreateFirstName(e.target.value)}
            size="small"
            disabled={createSaving}
          />
          <TextField
            label="Nom"
            required
            value={createLastName}
            onChange={(e) => setCreateLastName(e.target.value)}
            size="small"
            disabled={createSaving}
          />
          <TextField
            label="E-mail"
            type="email"
            value={createEmail}
            onChange={(e) => setCreateEmail(e.target.value)}
            size="small"
            disabled={createSaving}
          />
          <TextField
            label="Téléphone"
            value={createPhone}
            onChange={(e) => setCreatePhone(e.target.value)}
            size="small"
            disabled={createSaving}
          />
          <TextField
            label="Academy"
            value={createAcademy}
            onChange={(e) => setCreateAcademy(e.target.value)}
            size="small"
            disabled={createSaving}
          />
          <TextField
            label="CAP"
            value={createCap}
            onChange={(e) => setCreateCap(e.target.value)}
            size="small"
            disabled={createSaving}
          />
          <TextField
            label="Région"
            value={createRegion}
            onChange={(e) => setCreateRegion(e.target.value)}
            size="small"
            disabled={createSaving}
          />
          <TextField
            label="Commune"
            value={createCommune}
            onChange={(e) => setCreateCommune(e.target.value)}
            size="small"
            disabled={createSaving}
          />
          <TextField
            label="Spécialité"
            value={createSpeciality}
            onChange={(e) => setCreateSpeciality(e.target.value)}
            size="small"
            disabled={createSaving}
          />
          <FormControl size="small">
            <InputLabel id="create-role-label">Rôle</InputLabel>
            <Select
              labelId="create-role-label"
              label="Rôle"
              value={createRole}
              onChange={(e) => setCreateRole(e.target.value)}
              disabled={createSaving}
            >
              <MenuItem value="admin">Admin</MenuItem>
              <MenuItem value="drh">DRH</MenuItem>
              <MenuItem value="cap">CAP</MenuItem>
              <MenuItem value="responsible">Responsable</MenuItem>
              <MenuItem value="teacher">Enseignant</MenuItem>
              <MenuItem value="cgs">CGS</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small">
            <InputLabel id="create-status-label">Statut</InputLabel>
            <Select
              labelId="create-status-label"
              label="Statut"
              value={createStatus}
              onChange={(e) => setCreateStatus(e.target.value as 'active' | 'inactive')}
              disabled={createSaving}
            >
              <MenuItem value="active">Actif</MenuItem>
              <MenuItem value="inactive">Inactif</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeCreateUser} color="inherit" disabled={createSaving}>
            Annuler
          </Button>
          <Button
            onClick={() => void handleSaveCreateUser()}
            variant="contained"
            disabled={createSaving}
          >
            {createSaving ? 'Création…' : 'Créer'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(editUser)} onClose={closeEditDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Modifier l'utilisateur</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField
            label="Auth UID"
            value={editAuthUid}
            onChange={(e) => setEditAuthUid(e.target.value)}
            size="small"
            helperText="UID Firebase Auth lié à ce profil"
          />
          <TextField
            label="Prénom"
            value={editFirstName}
            onChange={(e) => setEditFirstName(e.target.value)}
            size="small"
          />
          <TextField
            label="Nom"
            value={editLastName}
            onChange={(e) => setEditLastName(e.target.value)}
            size="small"
          />
          <TextField
            label="E-mail"
            type="email"
            value={editEmail}
            onChange={(e) => setEditEmail(e.target.value)}
            size="small"
            autoComplete="off"
          />
          <TextField
            label="Téléphone"
            value={editPhone}
            onChange={(e) => setEditPhone(e.target.value)}
            size="small"
          />
          <TextField
            label="Academy"
            value={editAcademy}
            onChange={(e) => setEditAcademy(e.target.value)}
            size="small"
          />
          <TextField
            label="CAP"
            value={editCap}
            onChange={(e) => setEditCap(e.target.value)}
            size="small"
          />
          <TextField
            label="Région"
            value={editRegion}
            onChange={(e) => setEditRegion(e.target.value)}
            size="small"
          />
          <TextField
            label="Commune"
            value={editCommune}
            onChange={(e) => setEditCommune(e.target.value)}
            size="small"
          />
          <TextField
            label="Spécialité"
            value={editSpeciality}
            onChange={(e) => setEditSpeciality(e.target.value)}
            size="small"
          />
          <FormControl size="small">
            <InputLabel id="edit-role-label">Rôle</InputLabel>
            <Select
              labelId="edit-role-label"
              label="Rôle"
              value={editRole}
              onChange={(e) => setEditRole(e.target.value)}
            >
              <MenuItem value="admin">Admin</MenuItem>
              <MenuItem value="drh">DRH</MenuItem>
              <MenuItem value="cap">CAP</MenuItem>
              <MenuItem value="responsible">Responsable</MenuItem>
              <MenuItem value="teacher">Enseignant</MenuItem>
              <MenuItem value="cgs">CGS</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small">
            <InputLabel id="edit-status-label">Statut</InputLabel>
            <Select
              labelId="edit-status-label"
              label="Statut"
              value={editStatus}
              onChange={(e) => setEditStatus(e.target.value as 'active' | 'inactive')}
            >
              <MenuItem value="active">Actif</MenuItem>
              <MenuItem value="inactive">Inactif</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEditDialog} color="inherit">
            Annuler
          </Button>
          <Button onClick={handleSaveUser} variant="contained">
            Enregistrer
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

